import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  ScanLine,
  BarChart3,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LandingNavigation } from "@/components/LandingNavigation";
import { useAuthStore } from "@/stores/authStore";
import { useScanStore } from "@/stores/scanStore";
import { formatRelativeTime, formatRepositoryName } from "@/utils/formatters";
import scanService from "@/services/scans";

const features = [
  {
    icon: Shield,
    title: "Comprehensive Security Analysis",
    description:
      "Multi-scanner approach using Trivy, Hadolint, Semgrep, and Safety for complete coverage.",
  },
  {
    icon: ScanLine,
    title: "Real-time Scanning",
    description:
      "Live progress updates and instant notifications when scans complete.",
  },
  {
    icon: BarChart3,
    title: "Detailed Reports",
    description:
      "Rich analytics and exportable reports with actionable security insights.",
  },
];

const supportedPlatforms = [
  { name: "GitHub", icon: "🐙", supported: true },
  { name: "GitLab", icon: "🦊", supported: true },
  { name: "Bitbucket", icon: "🪣", supported: true },
];

export function HomePage() {
  const { isAuthenticated } = useAuthStore();
  const { scans, setScans, setLoading } = useScanStore();
  const [recentScans, setRecentScans] = useState(scans.slice(0, 3));

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadRecentScans = async () => {
      try {
        setLoading(true);
        const response = await scanService.getScans({
          page: 1,
          per_page: 3,
          sort: { field: "created_at", direction: "desc" },
        });
        setScans(response.items);
        setRecentScans(response.items);
      } catch (error) {
        console.error("Failed to load recent scans:", error);
      } finally {
        setLoading(false);
      }
    };

    if (scans.length === 0) {
      loadRecentScans();
    } else {
      setRecentScans(scans.slice(0, 3));
    }
  }, [isAuthenticated, scans, setScans, setLoading]);

  const heroContent = (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-8 flex justify-center">
              <Shield className="h-16 w-16 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl">
              Secure Your Code with
              <span className="text-blue-600"> RiskLens</span>
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Comprehensive security scanning for your repositories. Detect
              vulnerabilities, secrets, and security issues before they reach
              production.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button size="lg" asChild>
                <Link to="/start-scan">
                  <ScanLine className="h-5 w-5 mr-2" />
                  Start Free Scan
                </Link>
              </Button>
              {!isAuthenticated && (
                <Button variant="ghost" asChild>
                  <Link to="/examples">
                    View Examples <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              )}
            </div>
          </div>

          {/* Platform Support */}
          <div className="mt-16">
            <p className="text-center text-sm font-semibold leading-6 text-gray-600 dark:text-gray-400">
              Supports public repositories from
            </p>
            <div className="mt-6 flex justify-center items-center gap-8">
              {supportedPlatforms.map((platform) => (
                <div
                  key={platform.name}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  <span className="text-2xl">{platform.icon}</span>
                  <span>{platform.name}</span>
                  {platform.supported && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm font-semibold leading-6 text-gray-600 dark:text-gray-400">
              <br></br>Private repos coming soon!
            </p>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-3xl">
          <div className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-blue-300 to-blue-600 opacity-20" />
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Complete Security Coverage
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-300">
              Our multi-scanner approach ensures no security issue goes
              undetected.
            </p>
          </div>

          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {features.map((feature, index) => (
              <Card key={index}>
                <CardHeader>
                  <feature.icon className="h-8 w-8 text-blue-600" />
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Scans Section — authenticated only */}
      {isAuthenticated && recentScans.length > 0 && (
        <div className="py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Recent Scans
                </h2>
                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">
                  Your latest security scan results
                </p>
              </div>
              <Button variant="ghost" asChild>
                <Link to="/history">
                  View All <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {recentScans.map((scan) => (
                <Card
                  key={scan.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {formatRepositoryName(scan.repository_url)}
                      </CardTitle>
                      <Badge
                        variant={
                          scan.status === "completed"
                            ? "success"
                            : scan.status === "failed"
                            ? "destructive"
                            : scan.status === "running"
                            ? "warning"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {scan.status}
                      </Badge>
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <span>{formatRelativeTime(scan.created_at)}</span>
                      <span>•</span>
                      <span className="capitalize">{scan.branch}</span>
                    </CardDescription>
                  </CardHeader>

                  <CardContent>
                    {scan.summary && (
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {scan.summary.total_findings} findings
                        </span>
                        <span className="text-lg font-semibold">
                          {scan.summary.risk_score.toFixed(1)}/10
                        </span>
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      asChild
                    >
                      <Link to={`/scan/${scan.id}`}>
                        View Details <ArrowRight className="h-3 w-3 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="bg-blue-600">
        <div className="px-6 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ready to secure your code?
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-blue-100">
              Start with a free security scan and see what vulnerabilities might
              be lurking in your code.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/start-scan">Start Free Scan</Link>
              </Button>
              <Button
                variant="ghost"
                className="text-white hover:text-white hover:bg-blue-700"
                asChild
              >
                {isAuthenticated ? (
                  <Link to="/dashboard">
                    View Dashboard <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                ) : (
                  <Link to="/register">
                    Sign Up <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <>
        <LandingNavigation />
        {heroContent}
      </>
    );
  }

  return heroContent;
}
